import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { Plus, Edit, Trash2, Save, X, Book, Eye, Clock, Video, FileText } from 'lucide-react';

const LessonManagement = () => {
    const [lessons, setLessons] = useState([]);
    const [levels, setLevels] = useState([]);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingLesson, setEditingLesson] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState('all');
    const [selectedTerm, setSelectedTerm] = useState('all');
    const [formData, setFormData] = useState({
        term_id: '',
        title: '',
        description: '',
        lesson_number: '',
        display_order: '',
        duration_minutes: 30,
        is_free: false,
        thumbnail: '',
        video_url: '',
        content_html: '',
        learning_objectives: '',
        tags: []
    });
    const [errors, setErrors] = useState({});

    const [hobbyTags, setHobbyTags] = useState([
        'building', 'math-logic', 'space', 'experiments', 'coding', 'nature', 'how-things-work', 'design'
    ]);

    useEffect(() => {
        if (!formData.tags) {
            setFormData(prev => ({ ...prev, tags: [] }));
        }
    }, []);

    const handleAddTag = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const newTag = e.target.value.trim();
            if (newTag && !formData.tags.includes(newTag)) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, newTag]
                }));
            }
            e.target.value = '';
        }
    };

    const handleRemoveTag = (tag) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(t => t !== tag)
        }));
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedLevel !== 'all') {
            fetchTermsByLevel(selectedLevel);
        }
        fetchLessons();
    }, [selectedLevel, selectedTerm]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [levelsData, lessonsData] = await Promise.all([
                adminService.getLevels(),
                adminService.getLessons()
            ]);
            setLevels(levelsData.levels);
            setLessons(lessonsData.lessons);
        } catch (err) {
            alert('Failed to fetch data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchTermsByLevel = async (levelId) => {
        try {
            const response = await adminService.getTerms({ level_id: levelId });
            setTerms(response.terms);
        } catch (err) {
            console.error('Failed to fetch terms:', err);
        }
    };

    const fetchLessons = async () => {
        try {
            const params = {};
            if (selectedTerm !== 'all') {
                params.term_id = selectedTerm;
            }
            const response = await adminService.getLessons(params);
            setLessons(response.lessons);
        } catch (err) {
            alert('Failed to fetch lessons: ' + err.message);
        }
    };

    const handleOpenModal = (lesson = null) => {
        if (lesson) {
            setEditingLesson(lesson);
            setFormData({
                term_id: lesson.term_id,
                title: lesson.title,
                description: lesson.description || '',
                lesson_number: lesson.lesson_number,
                display_order: lesson.display_order,
                duration_minutes: lesson.duration_minutes || 30,
                is_free: lesson.is_free || false,
                thumbnail: lesson.thumbnail || '',
                video_url: lesson.video_url || '',
                content_html: lesson.content_html || '',
                learning_objectives: lesson.learning_objectives || '',
                tags: Array.isArray(lesson.tags) ? lesson.tags : lesson.tags ? JSON.parse(lesson.tags) : []
            });
        } else {
            setEditingLesson(null);
            setFormData({
                term_id: selectedTerm !== 'all' ? selectedTerm : '',
                title: '',
                description: '',
                lesson_number: '',
                display_order: lessons.length + 1,
                duration_minutes: 30,
                is_free: false,
                thumbnail: '',
                video_url: '',
                content_html: '',
                learning_objectives: '',
                tags: []
            });
        }
        setErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingLesson(null);
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.term_id) {
            newErrors.term_id = 'Term is required';
        }

        if (!formData.title.trim()) {
            newErrors.title = 'Lesson title is required';
        }

        if (!formData.lesson_number || formData.lesson_number < 1) {
            newErrors.lesson_number = 'Valid lesson number is required';
        }

        if (!formData.display_order || formData.display_order < 1) {
            newErrors.display_order = 'Display order is required';
        }

        if (formData.duration_minutes < 1) {
            newErrors.duration_minutes = 'Duration must be at least 1 minute';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            const submitData = {
                ...formData,
                term_id: parseInt(formData.term_id),
                lesson_number: parseInt(formData.lesson_number),
                display_order: parseInt(formData.display_order),
                duration_minutes: parseInt(formData.duration_minutes)
            };

            if (editingLesson) {
                await adminService.updateLesson(editingLesson.id, submitData);
                alert('Lesson updated successfully!');
            } else {
                await adminService.createLesson(submitData);
                alert('Lesson created successfully!');
            }

            handleCloseModal();
            fetchLessons();
        } catch (err) {
            alert('Failed to save lesson: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (lessonId, lessonTitle) => {
        if (!window.confirm(`Are you sure you want to delete "${lessonTitle}"?`)) {
            return;
        }

        try {
            setLoading(true);
            await adminService.deleteLesson(lessonId);
            alert('Lesson deleted successfully!');
            fetchLessons();
        } catch (err) {
            alert('Failed to delete lesson: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Lesson Management</h2>
                    <p className="text-gray-600">Create and manage curriculum lessons</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Level Filter */}
                    <select
                        value={selectedLevel}
                        onChange={(e) => {
                            setSelectedLevel(e.target.value);
                            setSelectedTerm('all');
                        }}
                        className="input-field"
                    >
                        <option value="all">All Levels</option>
                        {levels.map(level => (
                            <option key={level.id} value={level.id}>
                                {level.name}
                            </option>
                        ))}
                    </select>

                    {/* Term Filter */}
                    {selectedLevel !== 'all' && (
                        <select
                            value={selectedTerm}
                            onChange={(e) => setSelectedTerm(e.target.value)}
                            className="input-field"
                        >
                            <option value="all">All Terms</option>
                            {terms.map(term => (
                                <option key={term.id} value={term.id}>
                                    {term.name}
                                </option>
                            ))}
                        </select>
                    )}

                    <button
                        onClick={() => handleOpenModal()}
                        className="btn-primary flex items-center gap-2"
                        disabled={levels.length === 0}
                    >
                        <Plus className="w-5 h-5" />
                        Add Lesson
                    </button>
                </div>
            </div>

            {/* Lessons List */}
            {lessons.length === 0 ? (
                <div className="card text-center py-12">
                    <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No lessons found</p>
                    <button
                        onClick={() => handleOpenModal()}
                        className="btn-primary mt-4"
                        disabled={levels.length === 0}
                    >
                        Create First Lesson
                    </button>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {lessons.map(lesson => (
                        <div
                            key={lesson.id}
                            className="card hover:shadow-lg transition-all"
                        >
                            <div className="flex items-start gap-4">
                                {/* Thumbnail/Icon */}
                                <div className="flex-shrink-0">
                                    {lesson.thumbnail ? (
                                        <img
                                            src={lesson.thumbnail}
                                            alt={lesson.title}
                                            className="w-20 h-20 object-cover rounded-lg"
                                        />
                                    ) : (
                                        <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
                                            <Book className="w-10 h-10 text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <h3 className="font-bold text-lg line-clamp-2 mb-1">
                                                {lesson.title}
                                            </h3>
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                                <span>{lesson.level_name}</span>
                                                <span>•</span>
                                                <span>{lesson.term_name}</span>
                                                <span>•</span>
                                                <span>Lesson {lesson.lesson_number}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {lesson.description && (
                                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                                            {lesson.description}
                                        </p>
                                    )}

                                    {/* Meta Info */}
                                    <div className="flex flex-wrap items-center gap-3 text-sm mb-3">
                    <span className="flex items-center gap-1 text-gray-600">
                      <Clock className="w-4 h-4" />
                        {lesson.duration_minutes} min
                    </span>
                                        {lesson.video_url && (
                                            <span className="flex items-center gap-1 text-blue-600">
                        <Video className="w-4 h-4" />
                        Video
                      </span>
                                        )}
                                        {lesson.is_free && (
                                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                        FREE
                      </span>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleOpenModal(lesson)}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium"
                                        >
                                            <Edit className="w-4 h-4" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(lesson.id, lesson.title)}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8">
                        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between rounded-t-2xl">
                            <h2 className="text-2xl font-bold">
                                {editingLesson ? 'Edit Lesson' : 'Create New Lesson'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                            {/* Level & Term Selection */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Level <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={levels.find(l => terms.find(t => t.id === parseInt(formData.term_id))?.level_id === l.id)?.id || ''}
                                        onChange={(e) => {
                                            fetchTermsByLevel(e.target.value);
                                            setFormData(prev => ({ ...prev, term_id: '' }));
                                        }}
                                        className="input-field"
                                        disabled={editingLesson}
                                    >
                                        <option value="">Select a level</option>
                                        {levels.map(level => (
                                            <option key={level.id} value={level.id}>
                                                {level.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Term <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="term_id"
                                        value={formData.term_id}
                                        onChange={handleChange}
                                        className={`input-field ${errors.term_id ? 'border-red-500' : ''}`}
                                        disabled={editingLesson}
                                    >
                                        <option value="">Select a term</option>
                                        {terms.map(term => (
                                            <option key={term.id} value={term.id}>
                                                {term.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.term_id && (
                                        <p className="text-red-600 text-sm mt-1">{errors.term_id}</p>
                                    )}
                                </div>
                            </div>

                            {/* Lesson Title */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Lesson Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    className={`input-field ${errors.title ? 'border-red-500' : ''}`}
                                    placeholder="e.g., What is Science?"
                                />
                                {errors.title && (
                                    <p className="text-red-600 text-sm mt-1">{errors.title}</p>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    className="input-field"
                                    placeholder="Brief description of the lesson..."
                                />
                            </div>

                            {/* Lesson Details Grid */}
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Lesson Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="lesson_number"
                                        value={formData.lesson_number}
                                        onChange={handleChange}
                                        min="1"
                                        className={`input-field ${errors.lesson_number ? 'border-red-500' : ''}`}
                                    />
                                    {errors.lesson_number && (
                                        <p className="text-red-600 text-sm mt-1">{errors.lesson_number}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Display Order <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="display_order"
                                        value={formData.display_order}
                                        onChange={handleChange}
                                        min="1"
                                        className={`input-field ${errors.display_order ? 'border-red-500' : ''}`}
                                    />
                                    {errors.display_order && (
                                        <p className="text-red-600 text-sm mt-1">{errors.display_order}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Duration (minutes)
                                    </label>
                                    <input
                                        type="number"
                                        name="duration_minutes"
                                        value={formData.duration_minutes}
                                        onChange={handleChange}
                                        min="1"
                                        className="input-field"
                                    />
                                </div>
                            </div>

                            {/* Free Lesson Checkbox */}
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="is_free"
                                    checked={formData.is_free}
                                    onChange={handleChange}
                                    className="rounded"
                                    id="is_free"
                                />
                                <label htmlFor="is_free" className="text-sm font-medium cursor-pointer">
                                    Make this lesson free (for marketing/trial purposes)
                                </label>
                            </div>

                            {/* Media URLs */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Video URL
                                </label>
                                <input
                                    type="url"
                                    name="video_url"
                                    value={formData.video_url}
                                    onChange={handleChange}
                                    className="input-field"
                                    placeholder="https://youtube.com/..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Thumbnail URL
                                </label>
                                <input
                                    type="url"
                                    name="thumbnail"
                                    value={formData.thumbnail}
                                    onChange={handleChange}
                                    className="input-field"
                                    placeholder="https://..."
                                />
                            </div>

                            {/* Lesson Content */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Lesson Content (HTML)
                                </label>
                                <textarea
                                    name="content_html"
                                    value={formData.content_html}
                                    onChange={handleChange}
                                    rows="6"
                                    className="input-field font-mono text-sm"
                                    placeholder="<p>Lesson content in HTML format...</p>"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    You can use basic HTML tags like p, h3, ul, li, strong, etc.
                                </p>
                            </div>

                            {/* Learning Objectives */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Learning Objectives
                                </label>
                                <textarea
                                    name="learning_objectives"
                                    value={formData.learning_objectives}
                                    onChange={handleChange}
                                    rows="3"
                                    className="input-field"
                                    placeholder="What students will learn in this lesson..."
                                />
                            </div>

                            {/* --- TAGS SECTION --- */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Tags (choose from hobbies or add new)
                                </label>

                                {/* Predefined hobby tags */}
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {hobbyTags.map(tag => (
                                        <button
                                            key={tag}
                                            type="button"
                                            onClick={() => {
                                                if (!formData.tags.includes(tag)) {
                                                    setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
                                                }
                                            }}
                                            className={`px-2 py-1 rounded-full text-xs border ${
                                                formData.tags.includes(tag)
                                                    ? 'bg-blue-500 text-white border-blue-500'
                                                    : 'bg-gray-100 text-gray-700 border-gray-200'
                                            }`}
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>

                                {/* Current selected tags */}
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {formData.tags.map(tag => (
                                        <span
                                            key={tag}
                                            className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1 text-xs"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                                className="ml-1 text-blue-700 hover:text-blue-900"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                {/* Custom tag input */}
                                <input
                                    type="text"
                                    placeholder="Press Enter to add a custom tag"
                                    onKeyDown={handleAddTag}
                                    className="input-field"
                                />
                            </div>
                            {/* --- END TAGS SECTION --- */}

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4 sticky bottom-0 bg-white border-t -mx-6 px-6 py-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            {editingLesson ? 'Update Lesson' : 'Create Lesson'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LessonManagement;